import React, { useState, useEffect, useMemo } from 'react';
import {
  Spin,
  Table,
  Select,
  Button,
  DatePicker,
  Input,
  Tag,
  Card,
  Row,
  Col,
  Dropdown,
} from 'antd';
import {
  DownOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { type AppDispatch } from '../../../redux/store';
import { getBillingTransactionsAction } from '../store/expenseActions';
import {
  type BillingBalance,
  type BillingLedgerEntry,
} from '../store/expenseTypes';
import fmt from 'indian-number-format';

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ExcelJS from 'exceljs';

// Amounts can carry paise, so round to 2 decimals before formatting
const formatAmount = (value: number): string =>
  fmt.format(Math.round(value * 100) / 100);

const categoryLabel = (record: BillingLedgerEntry): string =>
  `${record.category}${record.reportInPnl === true ? ' (P&L)' : ''}${
    record.entryType === 'Expense' && record.type !== record.category
      ? ` - ${record.type}`
      : ''
  }`;

export default function BillingTransactionsIndex(): JSX.Element {
  const { RangePicker } = DatePicker;
  const dateFormat = 'YYYY-MM-DD';

  const dispatch = useDispatch<AppDispatch>();

  const { billingTransactions, billingBalances }: any = useSelector<any>(
    (state) => state?.expense
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [branchOptions, setBranchOptions] = useState<
    Array<{
      label: string;
      value: string;
    }>
  >([]);

  const [branch, setBranch] = useState<string>('');

  const [startDate, setStartDate] = useState<string>(
    dayjs().startOf('month').format(dateFormat)
  );
  const [endDate, setEndDate] = useState<string>(dayjs().format(dateFormat));

  useEffect(() => {
    const branches = localStorage.getItem('branches');
    if (branches != null) {
      const parsedBranches = JSON.parse(branches);
      const branchOptionTemp: Array<{ label: string; value: string }> =
        parsedBranches.map((branch: { code: string; id: string }) => ({
          label: branch.code,
          value: branch.id,
        }));
      branchOptionTemp.push({
        label: 'All Branches',
        value: 'all',
      });
      setBranchOptions(branchOptionTemp);
      setBranch(String(parsedBranches[0].id));
    }
  }, []);

  useEffect(() => {
    if (branch !== '') {
      void getTransactions();
    }
  }, [branch]);

  const getTransactions = async (): Promise<void> => {
    setIsLoading(true);
    await getBillingTransactionsAction(dispatch, {
      branch,
      startDate,
      endDate,
    });
    setIsLoading(false);
  };

  const onChangeDate = (values: any, formatString: [string, string]) => {
    setStartDate(formatString[0]);
    setEndDate(formatString[1]);
  };

  const [searchText, setSearchText] = useState<string>('');
  const [entryTypeFilter, setEntryTypeFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');

  const categoryOptions = useMemo(
    () =>
      Array.from(
        new Set(
          (billingBalances as BillingBalance[]).map(
            (balance) => balance.category
          )
        )
      )
        .sort()
        .map((category) => ({ label: category, value: category })),
    [billingBalances]
  );

  const filteredTransactions = useMemo(() => {
    const search = searchText.trim().toLowerCase();
    return (billingTransactions as BillingLedgerEntry[]).filter(
      (record) =>
        (entryTypeFilter === '' || record.entryType === entryTypeFilter) &&
        (categoryFilter === '' || record.category === categoryFilter) &&
        (search === '' ||
          [record.description, record.type, record.category].some((field) =>
            (field ?? '').toLowerCase().includes(search)
          ) ||
          String(record.amount).includes(search))
    );
  }, [billingTransactions, searchText, entryTypeFilter, categoryFilter]);

  // Grouped by category A-Z, then branch (remaining is a per branch + category balance, so mixing
  // branches would make it jump); within a group the rows keep the server's newest-first order
  const groupedTransactions = useMemo(
    () =>
      [...filteredTransactions].sort((a, b) => {
        const byCategory = a.category.localeCompare(b.category);
        return byCategory !== 0
          ? byCategory
          : String(a.branch).localeCompare(String(b.branch));
      }),
    [filteredTransactions]
  );

  // Opening and remaining of each category, summed over branches (in paise to avoid float drift).
  // They are the category's balances, so the search and type filters must not change them.
  const balancesByCategory = useMemo(() => {
    const balances = new Map<string, { opening: number; remaining: number }>();
    for (const balance of billingBalances as BillingBalance[]) {
      const total = balances.get(balance.category) ?? { opening: 0, remaining: 0 };
      total.opening += Math.round(Number(balance.opening) * 100);
      total.remaining += Math.round(Number(balance.closing) * 100);
      balances.set(balance.category, total);
    }
    return balances;
  }, [billingBalances]);

  // Debit and credit of each category over the selected date range. Without a search or type
  // filter, categories with only an opening balance are listed too, so the totals add up.
  const categoryTotals = useMemo(() => {
    const totals = new Map<string, { debit: number; credit: number }>();
    for (const record of groupedTransactions) {
      const total = totals.get(record.category) ?? { debit: 0, credit: 0 };
      total.debit += Math.round(Number(record.debit) * 100);
      total.credit += Math.round(Number(record.credit) * 100);
      totals.set(record.category, total);
    }
    const isRowFiltered = searchText.trim() !== '' || entryTypeFilter !== '';
    const categories = isRowFiltered
      ? Array.from(totals.keys())
      : Array.from(balancesByCategory.keys()).filter(
          (category) => categoryFilter === '' || category === categoryFilter
        );
    return categories.sort().map((category) => {
      const { debit, credit } = totals.get(category) ?? { debit: 0, credit: 0 };
      const { opening, remaining } = balancesByCategory.get(category) ?? {
        opening: 0,
        remaining: 0,
      };
      return {
        category,
        opening: opening / 100,
        debit: debit / 100,
        credit: credit / 100,
        remaining: remaining / 100,
      };
    });
  }, [
    groupedTransactions,
    balancesByCategory,
    searchText,
    entryTypeFilter,
    categoryFilter,
  ]);

  const totalOpening =
    categoryTotals.reduce(
      (sum, record) => sum + Math.round(record.opening * 100),
      0
    ) / 100;

  const totalDebit = categoryTotals.reduce(
    (sum, record) => sum + record.debit,
    0
  );
  const totalCredit = categoryTotals.reduce(
    (sum, record) => sum + record.credit,
    0
  );
  const totalRemaining =
    categoryTotals.reduce(
      (sum, record) => sum + Math.round(record.remaining * 100),
      0
    ) / 100;

  const branchCodes = useMemo(
    () =>
      new Map(
        branchOptions.map((option) => [option.value, option.label] as const)
      ),
    [branchOptions]
  );

  const columns = [
    {
      title: 'S.No',
      dataIndex: 'sno',
      render: (_: any, record: BillingLedgerEntry, index: number) => index + 1,
      width: 60,
    },
    ...(branch === 'all'
      ? [
          {
            title: 'Branch',
            dataIndex: 'branch',
            key: 'branch',
            render: (_: any, record: BillingLedgerEntry) => (
              <p>{branchCodes.get(String(record.branch)) ?? ''}</p>
            ),
          },
        ]
      : []),
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (_: any, record: BillingLedgerEntry) => (
        <p>{dayjs(record.date).format('DD-MM-YYYY')}</p>
      ),
    },
    {
      title: 'Particulars',
      dataIndex: 'description',
      key: 'description',
      render: (_: any, record: BillingLedgerEntry) => (
        <p>{record.description}</p>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (_: any, record: BillingLedgerEntry) => (
        <p>{categoryLabel(record)}</p>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'entryType',
      key: 'entryType',
      render: (_: any, record: BillingLedgerEntry) => (
        <Tag color={record.entryType === 'Expense' ? 'red' : 'green'}>
          {record.entryType}
        </Tag>
      ),
    },
    {
      title: 'Debit',
      dataIndex: 'debit',
      key: 'debit',
      align: 'right' as const,
      render: (_: any, record: BillingLedgerEntry) => (
        <p>{record.debit > 0 ? formatAmount(record.debit) : ''}</p>
      ),
    },
    {
      title: 'Credit',
      dataIndex: 'credit',
      key: 'credit',
      align: 'right' as const,
      render: (_: any, record: BillingLedgerEntry) => (
        <p>{record.credit > 0 ? formatAmount(record.credit) : ''}</p>
      ),
    },
    {
      title: 'Remaining',
      dataIndex: 'remainingAmount',
      key: 'remainingAmount',
      align: 'right' as const,
      render: (_: any, record: BillingLedgerEntry) => (
        <p
          style={{
            color: (record.remainingAmount ?? 0) < 0 ? 'red' : 'green',
            fontWeight: 700,
          }}
        >
          {formatAmount(record.remainingAmount ?? 0)}
        </p>
      ),
    },
    {
      title: 'Created Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (_: any, record: BillingLedgerEntry) => (
        <p>
          {dayjs(record.createdAt ?? record.date).format('DD-MM-YYYY hh:mm A')}
        </p>
      ),
    },
  ];

  const categoryColumns = [
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
    },
    {
      title: 'Opening Remaining',
      dataIndex: 'opening',
      key: 'opening',
      render: (value: number) => (
        <p style={{ color: value < 0 ? 'red' : 'green' }}>
          {formatAmount(value)}
        </p>
      ),
    },
    {
      title: 'Debit',
      dataIndex: 'debit',
      key: 'debit',
      render: (value: number) => (
        <p style={{ color: 'red' }}>{formatAmount(value)}</p>
      ),
    },
    {
      title: 'Credit',
      dataIndex: 'credit',
      key: 'credit',
      render: (value: number) => (
        <p style={{ color: 'green' }}>{formatAmount(value)}</p>
      ),
    },
    {
      title: 'Remaining',
      dataIndex: 'remaining',
      key: 'remaining',
      render: (value: number) => (
        <p style={{ color: value < 0 ? 'red' : 'green', fontWeight: 700 }}>
          {formatAmount(value)}
        </p>
      ),
    },
  ];

  // The PDF holds what is on screen: the current filters, grouped by category
  const generatePDF = (): void => {
    const branchLabel =
      branchOptions.find((option) => option.value === branch)?.label ?? '';
    const showBranch = branch === 'all';

    // eslint-disable-next-line new-cap
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.setFontSize(18);
    doc.text('Billing Transactions', 14, 20);
    doc.setFontSize(10);
    doc.text(`Branch: ${branchLabel}   |   ${startDate} to ${endDate}`, 14, 27);

    doc.setFontSize(12);
    doc.text('Total', 14, 38);
    autoTable(doc, {
      startY: 42,
      theme: 'grid',
      head: [
        [
          'Opening Remaining',
          'Total Debit (Expense)',
          'Total Credit (Billing)',
          'Remaining',
        ],
      ],
      body: [
        [
          formatAmount(totalOpening),
          formatAmount(totalDebit),
          formatAmount(totalCredit),
          formatAmount(totalRemaining),
        ],
      ],
      styles: { cellPadding: 3, fontSize: 9, halign: 'center' },
    });

    let y: number = (doc as any).lastAutoTable.finalY + 12;
    doc.text('Category-wise Summary', 14, y);
    autoTable(doc, {
      startY: y + 4,
      theme: 'grid',
      head: [['Category', 'Opening Remaining', 'Debit', 'Credit', 'Remaining']],
      body: categoryTotals.map((total) => [
        total.category,
        formatAmount(total.opening),
        formatAmount(total.debit),
        formatAmount(total.credit),
        formatAmount(total.remaining),
      ]),
      styles: { cellPadding: 3, fontSize: 9, halign: 'center' },
    });

    categoryTotals.forEach(({ category }) => {
      const rows = groupedTransactions.filter(
        (record) => record.category === category
      );
      if (rows.length === 0) return;
      y = (doc as any).lastAutoTable.finalY + 12;
      if (y > doc.internal.pageSize.getHeight() - 30) {
        doc.addPage();
        y = 20;
      }
      doc.setFontSize(12);
      doc.text(category, 14, y);
      autoTable(doc, {
        startY: y + 4,
        theme: 'grid',
        head: [
          [
            'S.No',
            'Date',
            ...(showBranch ? ['Branch'] : []),
            'Particulars',
            'Category',
            'Type',
            'Debit',
            'Credit',
            'Remaining',
          ],
        ],
        body: rows.map((record, idx) => [
          idx + 1,
          dayjs(record.date).format('DD-MM-YYYY'),
          ...(showBranch ? [branchCodes.get(String(record.branch)) ?? ''] : []),
          record.description ?? '',
          categoryLabel(record),
          record.entryType,
          record.debit > 0 ? formatAmount(record.debit) : '',
          record.credit > 0 ? formatAmount(record.credit) : '',
          formatAmount(record.remainingAmount ?? 0),
        ]),
        styles: {
          cellPadding: 3,
          fontSize: 7,
          valign: 'middle',
          halign: 'center',
        },
      });
    });

    doc.save(`Billing-Transactions-${branchLabel}-${startDate}-${endDate}.pdf`);
  };

  // Same content as the PDF; amounts stay numbers so they can be summed and filtered in Excel
  const generateExcel = async (): Promise<void> => {
    const branchLabel =
      branchOptions.find((option) => option.value === branch)?.label ?? '';
    const showBranch = branch === 'all';
    const amountFormat = '#,##0.00';
    const workbook = new ExcelJS.Workbook();

    const addTitle = (sheet: ExcelJS.Worksheet, title: string): void => {
      sheet.addRow([localStorage.getItem('organizationName') ?? '']).font = {
        bold: true,
        size: 14,
      };
      sheet.addRow([title]).font = { bold: true, size: 12 };
      sheet.addRow([`Branch: ${branchLabel} | ${startDate} to ${endDate}`]);
      sheet.addRow([]);
    };
    const addHeader = (sheet: ExcelJS.Worksheet, headers: string[]): void => {
      const row = sheet.addRow(headers);
      row.font = { bold: true };
      row.eachCell((cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFF1F5F9' },
        };
      });
    };

    const summary = workbook.addWorksheet('Summary');
    addTitle(summary, 'Billing Transactions');
    addHeader(summary, [
      'Total',
      'Opening Remaining',
      'Debit (Expense)',
      'Credit (Billing)',
      'Remaining',
    ]);
    summary.addRow([
      'Total',
      totalOpening,
      totalDebit,
      totalCredit,
      totalRemaining,
    ]).font = {
      bold: true,
    };
    summary.addRow([]);
    summary.addRow(['Category-wise Summary']).font = { bold: true };
    addHeader(summary, [
      'Category',
      'Opening Remaining',
      'Debit',
      'Credit',
      'Remaining',
    ]);
    categoryTotals.forEach((total) => {
      summary.addRow([
        total.category,
        total.opening,
        total.debit,
        total.credit,
        total.remaining,
      ]);
    });
    summary.columns = [
      { width: 60 },
      { width: 18 },
      { width: 18 },
      { width: 18 },
      { width: 18 },
    ];
    [2, 3, 4, 5].forEach((column) => {
      summary.getColumn(column).numFmt = amountFormat;
    });

    const transactions = workbook.addWorksheet('Transactions');
    addTitle(transactions, 'Billing Transactions');
    const headers = [
      'S.No',
      'Date',
      ...(showBranch ? ['Branch'] : []),
      'Particulars',
      'Category',
      'Type',
      'Debit',
      'Credit',
      'Remaining',
      'Created Date',
    ];
    addHeader(transactions, headers);
    groupedTransactions.forEach((record, index) => {
      transactions.addRow([
        index + 1,
        dayjs(record.date).format('DD-MM-YYYY'),
        ...(showBranch ? [branchCodes.get(String(record.branch)) ?? ''] : []),
        record.description ?? '',
        categoryLabel(record),
        record.entryType,
        record.debit > 0 ? record.debit : null,
        record.credit > 0 ? record.credit : null,
        record.remainingAmount ?? 0,
        dayjs(record.createdAt ?? record.date).format('DD-MM-YYYY hh:mm A'),
      ]);
    });
    transactions.columns = headers.map((header) => ({
      width: ['Particulars', 'Category'].includes(header) ? 45 : 16,
    }));
    ['Debit', 'Credit', 'Remaining'].forEach((header) => {
      transactions.getColumn(headers.indexOf(header) + 1).numFmt =
        amountFormat;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Billing-Transactions-${branchLabel}-${startDate}-${endDate}.xlsx`;
    link.click();
    // revoking right away can cancel the download in some browsers
    setTimeout(() => {
      URL.revokeObjectURL(link.href);
    }, 1000);
  };

  return (
    <div className="org_transactions__page">
      <Spin spinning={isLoading} fullscreen />

      <div style={{ display: 'flex', flexDirection: 'row', gap: 32 }}>
        <Select
          value={branch}
          onChange={(value: string): void => {
            setBranch(value);
          }}
          style={{ width: 311, height: 56 }}
          placeholder="Select branch"
          options={branchOptions}
          className="expense__input"
        />
        <RangePicker
          className="rangepicker"
          onChange={onChangeDate}
          defaultValue={[dayjs(startDate), dayjs(endDate)]}
          format={dateFormat}
        />
        <Button onClick={getTransactions} className="search__button">
          <span>Search</span>
        </Button>
        {Boolean(categoryTotals.length) && (
          <Dropdown
            trigger={['click']}
            menu={{
              items: [
                {
                  key: 'excel',
                  label: 'Excel',
                  icon: <FileExcelOutlined style={{ color: '#1d6f42' }} />,
                },
                {
                  key: 'pdf',
                  label: 'PDF',
                  icon: <FilePdfOutlined style={{ color: '#dd1e2d' }} />,
                },
              ],
              onClick: ({ key }) => {
                if (key === 'excel') void generateExcel();
                else generatePDF();
              },
            }}
          >
            <Button className="search__button" icon={<DownloadOutlined />}>
              <span>Download</span>
              <DownOutlined style={{ fontSize: 10, marginLeft: 4 }} />
            </Button>
          </Dropdown>
        )}
      </div>
      {Boolean(billingBalances.length) && (
        <div style={{ width: '100%' }}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: 16,
              margin: '16px 0',
            }}
          >
            <Input.Search
              allowClear
              placeholder="Search particulars, category or amount"
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
              }}
              style={{ width: 320 }}
            />
            <Select
              value={entryTypeFilter}
              onChange={setEntryTypeFilter}
              style={{ width: 160 }}
              options={[
                { label: 'All Types', value: '' },
                { label: 'Expense', value: 'Expense' },
                { label: 'Billing', value: 'Billing' },
              ]}
            />
            <Select
              value={categoryFilter}
              onChange={setCategoryFilter}
              style={{ width: 320 }}
              showSearch
              options={[{ label: 'All Categories', value: '' }, ...categoryOptions]}
            />
          </div>
          <div style={{ marginBottom: 24 }}>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Card title="Total" size="small">
                  <Row gutter={16}>
                    <Col xs={24} sm={6}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 12, color: '#666' }}>
                          Opening Remaining
                        </div>
                        <div
                          style={{
                            fontSize: 16,
                            fontWeight: 'bold',
                            color: totalOpening < 0 ? 'red' : 'green',
                          }}
                        >
                          {formatAmount(totalOpening)}
                        </div>
                      </div>
                    </Col>
                    <Col xs={24} sm={6}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 12, color: '#666' }}>
                          Total Debit (Expense)
                        </div>
                        <div
                          style={{ fontSize: 16, fontWeight: 'bold', color: 'red' }}
                        >
                          {formatAmount(totalDebit)}
                        </div>
                      </div>
                    </Col>
                    <Col xs={24} sm={6}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 12, color: '#666' }}>
                          Total Credit (Billing)
                        </div>
                        <div
                          style={{
                            fontSize: 16,
                            fontWeight: 'bold',
                            color: 'green',
                          }}
                        >
                          {formatAmount(totalCredit)}
                        </div>
                      </div>
                    </Col>
                    <Col xs={24} sm={6}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 12, color: '#666' }}>
                          Remaining
                        </div>
                        <div
                          style={{
                            fontSize: 16,
                            fontWeight: 'bold',
                            color: totalRemaining < 0 ? 'red' : 'green',
                          }}
                        >
                          {formatAmount(totalRemaining)}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card>
              </Col>
              <Col span={24}>
                <Card title="Category-wise Summary" size="small">
                  <Table
                    dataSource={categoryTotals}
                    columns={categoryColumns}
                    rowKey="category"
                    pagination={false}
                    size="small"
                  />
                </Card>
              </Col>
            </Row>
          </div>
          <Table
            dataSource={groupedTransactions}
            columns={columns}
            rowKey={(record: BillingLedgerEntry) =>
              `${record.entryType}-${record._id}`
            }
          />
        </div>
      )}
      {billingBalances.length === 0 && (
        <div style={{ width: '100%' }}>
          <p>No billing transactions available for the branch</p>
        </div>
      )}
    </div>
  );
}
